import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Crosshair, User, Package, AlertTriangle, MapPin, Navigation, Trash2, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const MARKER_TYPES = [
  { 
    id: 'PERSON_OVERBOARD', 
    label: 'Person Overboard', 
    icon: User, 
    color: 'bg-red-500',
    description: 'Someone has fallen into the water'
  },
  { 
    id: 'DROPPED_ITEM', 
    label: 'Dropped Item', 
    icon: Package, 
    color: 'bg-amber-500',
    description: 'Mark where an item was dropped'
  },
  { 
    id: 'HAZARD', 
    label: 'Hazard', 
    icon: AlertTriangle, 
    color: 'bg-red-600',
    description: 'Mark a hazard location'
  },
  { 
    id: 'LAST_KNOWN', 
    label: 'Last Known Location', 
    icon: MapPin, 
    color: 'bg-indigo-500',
    description: 'Mark a reference point'
  },
];

const MOBMarkerModal = ({ 
  isOpen, 
  onClose, 
  onCreateMarker, 
  myLocation,
  emergencyMarkers = [],
  onClearMarker 
}) => {
  const [selectedType, setSelectedType] = useState('PERSON_OVERBOARD');
  const [label, setLabel] = useState('');
  const [broadcastToCrew, setBroadcastToCrew] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showActiveMarkers, setShowActiveMarkers] = useState(false);

  const handleCreate = async () => {
    if (!myLocation) return;
    
    setIsCreating(true);
    try {
      await onCreateMarker({
        lat: myLocation.lat,
        lon: myLocation.lon,
        marker_type: selectedType,
        label: label.trim() || null,
        broadcast_to_crew: broadcastToCrew
      });
      setLabel('');
      setSelectedType('PERSON_OVERBOARD');
      onClose();
    } catch (error) {
      console.error('Failed to create marker:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const myMarkers = emergencyMarkers.filter(m => m.is_mine);
  const crewMarkers = emergencyMarkers.filter(m => !m.is_mine);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crosshair className="w-5 h-5 text-amber-600" />
            Mark Emergency Location
          </DialogTitle>
          <DialogDescription>
            Create a marker visible to your crew. Useful for MOB (Man Overboard), dropped items, or hazards.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          {/* Toggle between Create and View */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={!showActiveMarkers ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowActiveMarkers(false)}
              className="flex-1"
            >
              Create New
            </Button>
            <Button
              variant={showActiveMarkers ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowActiveMarkers(true)}
              className="flex-1"
            >
              Active ({emergencyMarkers.length})
            </Button>
          </div>

          {!showActiveMarkers ? (
            /* Create New Marker Form */
            <div className="space-y-4">
              {/* Marker Type Selection */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Marker Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {MARKER_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedType === type.id;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setSelectedType(type.id)}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          isSelected 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        data-testid={`mob-type-${type.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-8 h-8 ${type.color} rounded-lg flex items-center justify-center`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-medium text-sm">{type.label}</span>
                        </div>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Optional Label */}
              <div>
                <Label htmlFor="marker-label" className="text-sm font-medium">
                  Label (optional)
                </Label>
                <Input
                  id="marker-label"
                  placeholder="e.g., John's phone, Red fender"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  className="mt-1"
                  data-testid="mob-label-input"
                />
              </div>

              {/* Broadcast Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="broadcast-crew" className="text-sm font-medium">
                    Broadcast to Crew
                  </Label>
                  <p className="text-xs text-gray-500">
                    Your friends will see this marker and receive a notification
                  </p>
                </div>
                <Switch
                  id="broadcast-crew"
                  checked={broadcastToCrew}
                  onCheckedChange={setBroadcastToCrew}
                />
              </div>

              {/* Current Location Info */}
              {myLocation && (
                <div className="p-3 bg-cyan-50 rounded-lg">
                  <p className="text-xs text-cyan-700">
                    <Navigation className="w-3 h-3 inline mr-1" />
                    Marker will be placed at your current location:
                  </p>
                  <p className="text-xs text-cyan-600 font-mono mt-1">
                    {myLocation.lat.toFixed(5)}, {myLocation.lon.toFixed(5)}
                  </p>
                </div>
              )}

              {!myLocation && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs text-red-700">
                    Location required. Please enable location services.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* View Active Markers */
            <div className="space-y-4">
              {/* My Markers */}
              {myMarkers.length > 0 && (
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase">
                    Your Markers ({myMarkers.length})
                  </Label>
                  <div className="space-y-2 mt-2">
                    {myMarkers.map((marker) => (
                      <Card key={marker.id} className="border-amber-200">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 ${MARKER_TYPES.find(t => t.id === marker.marker_type)?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                                {marker.marker_type === 'PERSON_OVERBOARD' && <User className="w-4 h-4 text-white" />}
                                {marker.marker_type === 'DROPPED_ITEM' && <Package className="w-4 h-4 text-white" />}
                                {marker.marker_type === 'HAZARD' && <AlertTriangle className="w-4 h-4 text-white" />}
                                {marker.marker_type === 'LAST_KNOWN' && <MapPin className="w-4 h-4 text-white" />}
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {marker.label || MARKER_TYPES.find(t => t.id === marker.marker_type)?.label}
                                </p>
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDistanceToNow(new Date(marker.created_at))} ago
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-red-500 hover:text-red-600"
                              onClick={() => onClearMarker(marker.id)}
                              data-testid={`clear-marker-${marker.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Crew Markers */}
              {crewMarkers.length > 0 && (
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase">
                    Crew Markers ({crewMarkers.length})
                  </Label>
                  <div className="space-y-2 mt-2">
                    {crewMarkers.map((marker) => (
                      <Card key={marker.id} className="border-cyan-200">
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 ${MARKER_TYPES.find(t => t.id === marker.marker_type)?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                              {marker.marker_type === 'PERSON_OVERBOARD' && <User className="w-4 h-4 text-white" />}
                              {marker.marker_type === 'DROPPED_ITEM' && <Package className="w-4 h-4 text-white" />}
                              {marker.marker_type === 'HAZARD' && <AlertTriangle className="w-4 h-4 text-white" />}
                              {marker.marker_type === 'LAST_KNOWN' && <MapPin className="w-4 h-4 text-white" />}
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {marker.label || MARKER_TYPES.find(t => t.id === marker.marker_type)?.label}
                              </p>
                              <p className="text-xs text-gray-500">
                                By {marker.user_display_name || 'Crew member'} • {formatDistanceToNow(new Date(marker.created_at))} ago
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {emergencyMarkers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Crosshair className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No active markers</p>
                  <p className="text-xs">Create one to mark an emergency location</p>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            {showActiveMarkers ? 'Close' : 'Cancel'}
          </Button>
          {!showActiveMarkers && (
            <Button 
              onClick={handleCreate} 
              disabled={!myLocation || isCreating}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="create-mob-marker"
            >
              {isCreating ? 'Creating...' : 'Mark Location'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MOBMarkerModal;
